import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  CalendarDays,
  ShoppingCart,
  Package,
  Users,
  BarChart3,
  LogOut,
  X,
  ChevronDown,
  ChevronRight,
  Briefcase,
  MapPin,
  Timer,
  Compass,
  ClipboardList,
  Settings,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: string[];
  children?: NavChild[];
}

interface NavChild {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Quando ausente, o subitem herda a permissão do item pai. */
  roles?: string[];
}

const navigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
    roles: ['ADMIN', 'FINANCEIRO', 'CAIXA', 'VISUALIZADOR'],
  },
  { name: 'Financeiro', href: '/financeiro', icon: Receipt, roles: ['ADMIN', 'FINANCEIRO'] },
  {
    name: 'Campanhas e Atividades',
    href: '/eventos',
    icon: CalendarDays,
    roles: [
      'ADMIN',
      'FINANCEIRO',
      'COORDENADOR_CAMPANHA',
      'COORDENADOR_EVENTO',
      'CAIXA',
      'VISUALIZADOR',
    ],
  },
  {
    name: 'Passeios LD-8',
    href: '/passeios',
    icon: MapPin,
    roles: ['ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO'],
  },
  {
    name: 'Cronômetro',
    href: '/cronometro',
    icon: Timer,
    roles: ['ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO'],
  },
  { name: 'PDV', href: '/pdv', icon: ShoppingCart, roles: ['ADMIN', 'CAIXA'] },
  { name: 'Estoque', href: '/estoque', icon: Package, roles: ['ADMIN'] },
  {
    name: 'Usuarios',
    href: '/usuarios',
    icon: Users,
    roles: ['ADMIN'],
    children: [
      { name: 'Usuarios', href: '/usuarios', icon: Users },
      { name: 'Cargos', href: '/usuarios/cargos', icon: Briefcase },
    ],
  },
  { name: 'Relatorios', href: '/relatorios', icon: BarChart3, roles: ['ADMIN', 'FINANCEIRO'] },
  { name: 'Configuracoes', href: '/configuracoes', icon: Settings, roles: ['ADMIN'] },
  {
    name: 'Teste de Aptidao',
    href: '/aptidao',
    icon: Compass,
    roles: [
      'ADMIN',
      'FINANCEIRO',
      'CAIXA',
      'COORDENADOR_CAMPANHA',
      'COORDENADOR_EVENTO',
      'VISUALIZADOR',
    ],
    children: [
      { name: 'Fazer o teste', href: '/aptidao', icon: Compass },
      { name: 'Resultados', href: '/aptidao/resultados', icon: ClipboardList, roles: ['ADMIN'] },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    if (location.pathname.startsWith('/usuarios')) return ['Usuarios'];
    if (location.pathname.startsWith('/aptidao')) return ['Teste de Aptidao'];
    return [];
  });

  const podeVer = (roles?: string[]) =>
    !roles || (user?.permissoes && roles.some((r) => user.permissoes.includes(r as any)));

  const filteredNavigation = navigation.filter(
    (item) => user?.permissoes && item.roles.some((r) => user.permissoes.includes(r as any))
  );

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const linkClass = (isActive: boolean) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-700 text-white'
        : 'text-primary-100 hover:bg-primary-500 hover:text-white'
    }`;

  return (
    <div
      className={`bg-primary-600 fixed inset-y-0 left-0 z-40 flex w-64 flex-col transition-transform duration-200 ease-in-out md:static md:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="border-primary-500 flex h-16 items-center justify-between border-b px-4">
        <h1 className="text-xl font-bold text-white">Portal LEO Clube</h1>
        <button
          onClick={onClose}
          className="text-primary-100 hover:bg-primary-500 rounded-lg p-1 hover:text-white md:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {filteredNavigation.map((item) =>
          item.children ? (
            <div key={item.name}>
              <button
                onClick={() => toggleExpanded(item.name)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname.startsWith(item.href)
                    ? 'bg-primary-700 text-white'
                    : 'text-primary-100 hover:bg-primary-500 hover:text-white'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1 text-left">{item.name}</span>
                {expandedItems.includes(item.name) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {expandedItems.includes(item.name) && (
                <div className="ml-4 mt-1 space-y-1">
                  {item.children
                    .filter((child) => podeVer(child.roles))
                    .map((child) => (
                      <NavLink
                        key={child.href}
                        to={child.href}
                        end={child.href === item.href}
                        onClick={onClose}
                        className={({ isActive }) => linkClass(isActive)}
                      >
                        <child.icon className="h-4 w-4" />
                        {child.name}
                      </NavLink>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={({ isActive }) => linkClass(isActive)}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          )
        )}
      </nav>

      <div className="border-primary-500 border-t p-4">
        <div className="text-primary-100 mb-3 text-sm">
          <p className="font-medium text-white">{user?.nome}</p>
          <p className="text-xs">{user?.permissoes?.join(', ')}</p>
        </div>
        <button
          onClick={() => logout()}
          className="text-primary-100 hover:bg-primary-500 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:text-white"
        >
          <LogOut className="h-5 w-5" />
          Sair
        </button>
      </div>
    </div>
  );
}
